import {
  fetchAndActivate as firebaseFetchAndActivate,
  getAll as firebaseGetAll,
  getRemoteConfig,
  getValue as firebaseGetValue,
  isSupported as remoteConfigIsSupported,
} from 'firebase/remote-config';

import {firebaseApp} from './firebase';

let remoteConfigInstance = null;
let remoteConfigSupportPromise = null;
let defaults = {};

const toRemoteConfigDefault = value => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value;
};

const asRemoteConfigValue = value => ({
  asBoolean: () => value === true || value === 'true',
  asNumber: () => Number(value) || 0,
  asString: () =>
    typeof value === 'string' ? value : JSON.stringify(value ?? ''),
});

const getDefaultValues = () =>
  Object.entries(defaults).reduce((result, [key, value]) => {
    result[key] = asRemoteConfigValue(toRemoteConfigDefault(value));
    return result;
  }, {});

const isRemoteConfigSupported = () => {
  if (!remoteConfigSupportPromise) {
    remoteConfigSupportPromise =
      typeof window === 'undefined'
        ? Promise.resolve(false)
        : remoteConfigIsSupported().catch(() => false);
  }

  return remoteConfigSupportPromise;
};

const getRemoteConfigInstance = async () => {
  if (!(await isRemoteConfigSupported())) {
    return null;
  }

  if (!remoteConfigInstance) {
    remoteConfigInstance = getRemoteConfig(firebaseApp);
  }

  return remoteConfigInstance;
};

const remoteConfig = () => ({
  fetchAndActivate: async () => {
    const instance = await getRemoteConfigInstance();

    if (!instance) {
      return false;
    }

    return firebaseFetchAndActivate(instance);
  },

  getAll: async () => {
    const instance = await getRemoteConfigInstance();

    if (!instance) {
      return getDefaultValues();
    }

    return firebaseGetAll(instance);
  },

  getValue: async key => {
    const instance = await getRemoteConfigInstance();

    if (!instance) {
      return getDefaultValues()[key] || asRemoteConfigValue('');
    }

    return firebaseGetValue(instance, key);
  },

  setDefaults: async nextDefaults => {
    defaults = nextDefaults || {};

    const instance = await getRemoteConfigInstance();

    if (instance) {
      instance.defaultConfig = Object.entries(defaults).reduce(
        (result, [key, value]) => {
          result[key] = toRemoteConfigDefault(value);
          return result;
        },
        {},
      );
    }
  },

  setConfigSettings: async settings => {
    const instance = await getRemoteConfigInstance();

    if (instance) {
      instance.settings = {
        ...instance.settings,
        ...settings,
      };
    }
  },
});

export default remoteConfig;
